import React, { useState, useEffect, useRef } from 'react';
import { Mic, XCircle } from 'lucide-react';

interface MicConfigurationPageProps {
  onBack: () => void;
}

export const MicConfigurationPage: React.FC<MicConfigurationPageProps> = ({ onBack }) => {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [audioLevel, setAudioLevel] = useState(0);
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [error, setError] = useState<string>('');
  const [isTesting, setIsTesting] = useState(false);
  
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number>();

  // Load devices on mount
  useEffect(() => {
    loadDevices();
    
    // Load saved device preference
    const savedDeviceId = localStorage.getItem('preferredMicId');
    if (savedDeviceId) {
      setSelectedDeviceId(savedDeviceId);
    }

    return () => {
      stopMonitoring();
    };
  }, []);

  const loadDevices = async () => {
    try {
      // Request permission first
      await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const deviceList = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = deviceList.filter(device => device.kind === 'audioinput');
      setDevices(audioInputs);
      
      // Set default device if none selected
      if (!selectedDeviceId && audioInputs.length > 0) {
        setSelectedDeviceId(audioInputs[0].deviceId);
      }
    } catch (err) {
      console.error('Error loading devices:', err);
      setError('Failed to access microphone. Please ensure you have granted permission.');
    }
  };

  const startMonitoring = async () => {
    try {
      stopMonitoring();
      
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: selectedDeviceId ? { exact: selectedDeviceId } : undefined }
      });
      
      streamRef.current = stream;
      
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyserRef.current = analyser;
      
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      
      updateAudioLevel();
    } catch (err) {
      console.error('Error starting monitoring:', err);
    }
  };

  const stopMonitoring = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    setAudioLevel(0);
  };

  const updateAudioLevel = () => {
    if (!analyserRef.current) return;
    
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);
    
    // Calculate average volume
    const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
    const normalized = Math.min(100, (average / 255) * 100 * 2);
    
    setAudioLevel(normalized);
    
    // Get waveform data for visualization
    const bars = Array.from(dataArray).slice(0, 50);
    setWaveformData(bars);
    
    animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
  };

  const handleDeviceChange = (deviceId: string) => {
    setSelectedDeviceId(deviceId);
    localStorage.setItem('preferredMicId', deviceId);
    setError('');
    
    // Restart monitoring with new device
    if (isTesting) {
      stopMonitoring();
      setTimeout(() => startMonitoring(), 100);
    }
  };

  const handleStartTest = () => {
    setIsTesting(true);
    startMonitoring();
  };

  const handleStopTest = () => {
    setIsTesting(false);
    stopMonitoring();
  };

  return (
    <>
      {/* Modal Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onBack} />
      
      {/* Modal Content */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-base-100 rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-base-300">
              <h1 className="text-2xl font-bold text-base-content">Microphone Configuration</h1>
              <button
                onClick={onBack}
                className="btn btn-ghost btn-circle btn-sm"
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">

              {/* Error Message */}
              {error && (
                <div className="alert alert-error">
                  <XCircle size={20} />
                  <span>{error}</span>
                </div>
              )}

              {/* Microphone Selection */}
              <div className="bg-base-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Mic size={24} className="text-primary" />
            <h2 className="text-xl font-semibold text-base-content">Select Microphone</h2>
          </div>
          
          <p className="text-base-content/70 mb-4">
            Choose the microphone you want to use for recording visits.
          </p>

          <select
            value={selectedDeviceId}
            onChange={(e) => handleDeviceChange(e.target.value)}
            className="select select-bordered w-full max-w-md"
          >
            {devices.length === 0 ? (
              <option>No microphones found</option>
            ) : (
              devices.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Microphone ${device.deviceId.substring(0, 8)}`}
                </option>
              ))
            )}
          </select>

          {devices.length === 0 && (
            <button
              onClick={loadDevices}
              className="btn btn-primary btn-sm mt-4"
            >
              Refresh Devices
            </button>
          )}
              </div>

              {/* Test Audio Input */}
              <div className="bg-base-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Mic size={24} className="text-primary" />
            <h2 className="text-xl font-semibold text-base-content">Test Audio Input</h2>
          </div>
          
          <p className="text-base-content/70 mb-6">
            Test your microphone to ensure it's working properly. Speak clearly and watch the audio levels.
          </p>

          {/* Test Button */}
          {!isTesting ? (
            <button
              onClick={handleStartTest}
              className="btn btn-primary mb-6"
              disabled={!selectedDeviceId || devices.length === 0}
            >
              <Mic size={20} />
              Start Testing
            </button>
          ) : (
            <button
              onClick={handleStopTest}
              className="btn btn-outline btn-secondary mb-6"
            >
              Stop Testing
            </button>
          )}

          {/* Waveform Visualization with Audio Level */}
          {isTesting && (
            <>
              <div className="bg-base-300 rounded-lg p-4">
                {/* Audio Level Label */}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium">Audio Level</span>
                  <span className="text-sm text-base-content/60">{Math.round(audioLevel)}%</span>
                </div>

                {/* Waveform with status overlay */}
                <div className="relative">
                  <div className="flex items-end justify-center gap-1 h-20">
                    {waveformData.length === 0 ? (
                      Array.from({ length: 50 }, (_, i) => (
                        <div
                          key={i}
                          className="w-1 bg-base-200 rounded-full animate-pulse"
                          style={{ 
                            height: '20%',
                            animationDelay: `${i * 20}ms`
                          }}
                        />
                      ))
                    ) : (
                      waveformData.map((value, i) => {
                        const height = Math.max((value / 255) * 100, 5);
                        return (
                          <div
                            key={i}
                            className="w-1 rounded-full transition-all duration-150"
                            style={{ 
                              height: `${height}%`,
                              backgroundColor: height > 5 ? '#10b981' : '#d1d5db'
                            }}
                          />
                        );
                      })
                    )}
                  </div>
                  
                  {/* Waiting for sound overlay */}
                  {audioLevel === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center bg-base-300/80">
                      <span className="text-sm text-base-content/60 animate-pulse">
                        Waiting for sound...
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Status Message - Fixed height container to prevent modal resize */}
              <div className="mt-4 min-h-[60px]">
                {audioLevel < 1 && (
                  <div className="alert alert-warning">
                    <span>
                      No sound detected. Try speaking louder or selecting a different microphone.
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
              </div>
            </div>
          </div>
      </div>
    </>
  );
};

